import { Box } from '@mui/material';
import { canSetEmployeeInactive } from 'api/requests/employee-requests';
import UPCheckbox from 'components/app-container/checkbox-with-label';
import UPDatepicker from 'components/datepicker';
import { CompanyRole } from 'constants/CompanyRole';
import { EmployeeStatus } from 'constants/EmployeeStatus';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useAppSelector } from 'store';
import { employeeSelector } from 'store/selectors/employee-selector';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  EMPLOYEES__CANNOT_SWITCH_TERMINATED,
  EMPLOYEES__ELIGIBLE,
  EMPLOYEES__EMPLOYMENT,
  EMPLOYEES__TERMINATED,
  EMPLOYEES__TERMINATED_REHIRE_INFO,
  EMPLOYEES__TERMINATION_DATE,
} from 'translations/constants';
import { getInputErrorText } from 'utils/helpers';
import {
  ErrorText,
  InfoText,
  RowContainer,
  SectionTitle,
  SubsectionDateContainer,
  SubsectionErrorContainer,
  SubsectionInputContainer,
} from './styles';
import { EmployeeTerminationProps } from './types';

const EligibilityAndTerminationDetails = (props: EmployeeTerminationProps) => {
  const employee = useAppSelector(employeeSelector);
  const isAdmin = useAppSelector(profileSelector)?.role === CompanyRole.ADMIN;

  const { companyId, control, errors, watch, getValues } = props;
  const [canBeInactive, setCanBeInactive] = useState<boolean>(false);
  const [terminationCheckCompleted, setTerminationCheckCompleted] = useState<boolean>(false);
  const watchTerminated = watch('terminated');
  const watchTerminatedDate = watch('terminatedDate');
  // A terminated employee only comes back through onboarding, so the checkbox is read-only and only the date stays editable.
  const isAlreadyTerminated = !!employee?.terminated;

  useEffect(() => {
    const canTerminateEmployee = async () => {
      const terminationDate = getValues('terminatedDate');
      const canInactive = await canSetEmployeeInactive(employee.id, +companyId, terminationDate);
      setCanBeInactive(canInactive);
      setTerminationCheckCompleted(true);
    };

    if (watchTerminated && watchTerminatedDate) {
      setTerminationCheckCompleted(false);
      canTerminateEmployee();
    } else {
      setCanBeInactive(false);
      setTerminationCheckCompleted(false);
    }
  }, [watchTerminated, watchTerminatedDate, employee, companyId, getValues]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <RowContainer>
        <SectionTitle>{t(EMPLOYEES__EMPLOYMENT)}</SectionTitle>
      </RowContainer>
      <SubsectionInputContainer>
        <Box sx={{ paddingBottom: '5px' }}>
          {/* only a SuperAdmin changes Eligible; while the employee requires action they decide with Approve or Not
              eligible instead */}
          {isAdmin && employee?.status !== EmployeeStatus.RequiresAction && (
            <Controller
              name="eligible"
              control={control}
              render={({ field }) => (
                <UPCheckbox label={t(EMPLOYEES__ELIGIBLE)} value={field.value} handleChange={field.onChange} />
              )}
            />
          )}
          <Controller
            name="terminated"
            control={control}
            render={({ field }) => (
              <UPCheckbox
                label={t(EMPLOYEES__TERMINATED)}
                value={field.value}
                handleChange={field.onChange}
                disabled={isAlreadyTerminated}
              />
            )}
          />
        </Box>
        <SubsectionDateContainer>
          {watchTerminated && (
            <Controller
              name="terminatedDate"
              control={control}
              rules={{ required: watchTerminated }}
              render={({ field }) => (
                <UPDatepicker
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                  }}
                  label={t(EMPLOYEES__TERMINATION_DATE)}
                  required={watchTerminated}
                  error={!!errors.terminatedDate}
                  helperText={getInputErrorText(errors, 'terminatedDate', undefined, 50)}
                  className="datepicker--white-background"
                />
              )}
            />
          )}
        </SubsectionDateContainer>
      </SubsectionInputContainer>
      {isAlreadyTerminated && (
        <SubsectionErrorContainer>
          <InfoText>{t(EMPLOYEES__TERMINATED_REHIRE_INFO)}</InfoText>
        </SubsectionErrorContainer>
      )}
      {watchTerminated && watchTerminatedDate && terminationCheckCompleted && !canBeInactive && (
        <SubsectionErrorContainer>
          <ErrorText>{t(EMPLOYEES__CANNOT_SWITCH_TERMINATED)}</ErrorText>
        </SubsectionErrorContainer>
      )}
    </Box>
  );
};

export default EligibilityAndTerminationDetails;
